import config from "../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import { clauseDraftsApi } from "./clauseDrafts";
import { sectionDraftsApi } from "./sectionDrafts";
import type { CommitDraftResponse } from "../../../api/src/documents/drafts/commit";
import type { GetDocumentLockResponse } from "../../../api/src/documents/drafts/lock";
import { documentsApi } from "./documents";

export const documentLocksApi = createApi({
  reducerPath: "documentLocksApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["documentLocks"],
  endpoints: (builder) => ({
    getDocumentLock: builder.query<GetDocumentLockResponse, string>({
      query: (documentId) => ({ url: `documents/${documentId}/lock` }),
      providesTags: ["documentLocks"],
    }),
    createDocumentLock: builder.mutation<
      { isLocked: boolean; lock: { createdById: string } },
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/lock`,
        method: "POST",
      }),
      invalidatesTags: ["documentLocks"],
    }),
    deleteDocumentLock: builder.mutation<{ isLocked: boolean }, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/lock`,
        method: "DELETE",
      }),
      invalidatesTags: ["documentLocks"],
    }),
    commitDraft: builder.mutation<CommitDraftResponse, string>({
      query: (documentId) => ({
        url: `documents/${documentId}/drafts/commit`,
        method: "POST",
      }),
      async onQueryStarted(_documentId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate draft caches so UI updates (e.g. status bars disappear)
          dispatch(clauseDraftsApi.util.invalidateTags(["clauseDrafts"]));
          dispatch(sectionDraftsApi.util.invalidateTags(["sectionDrafts"]));
          dispatch(
            documentsApi.util.invalidateTags(["documents", "document-history"]),
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),
    adminUnlockErase: builder.mutation<
      {
        sectionsSoftDeleted: number;
        clausesSoftDeleted: number;
        locksCleared: number;
      },
      string
    >({
      query: (documentId) => ({
        url: `documents/${documentId}/drafts/admin/unlock-erase`,
        method: "POST",
      }),
      async onQueryStarted(_documentId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(clauseDraftsApi.util.invalidateTags(["clauseDrafts"]));
          dispatch(sectionDraftsApi.util.invalidateTags(["sectionDrafts"]));
          dispatch(documentLocksApi.util.invalidateTags(["documentLocks"]));
        } catch (e) {
          console.error(e);
        }
      },
    }),
  }),
});

export const {
  useGetDocumentLockQuery,
  useCreateDocumentLockMutation,
  useDeleteDocumentLockMutation,
  useCommitDraftMutation,
  useAdminUnlockEraseMutation,
} = documentLocksApi;
